import StarIcon from "@mui/icons-material/Star";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import { Box, IconButton, Modal, Stack, Typography, Card, Chip, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment";
import { Postrequestcall } from "../../request/Postrequest";
import { English } from "../../constant/EnglishFallback";
import { FETCH_USER_FAVORITE_JOB_POSTS } from "../../constant/Apipath";
import { useSelector } from "react-redux";
import Loader from "../../common/Loader";
import { getJobFavoriteKey, getExpiryLabel } from "./favoriteUtils";

const getTradeItems = (job = {}) => {
  const rawTrades = job?.trades || job?.Trades || "";
  const rawTradeNames = job?.tradeName || job?.TradeName || "";

  if (Array.isArray(rawTrades)) return rawTrades;
  if (!rawTrades) return [];

  const tradeNames = rawTradeNames
    ? String(rawTradeNames)
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
    : [];

  return String(rawTrades)
    .split(",")
    .map((trade) => trade.trim())
    .filter(Boolean)
    .map((trade, index) => ({
      id: trade,
      tradeId: trade,
      tradeName: tradeNames[index] || ""
    }));
};

const FavoriteJobsModal = ({ open, onClose, reloadKey, onFavoriteToggle, favoriteLoadingKeys }) => {
  const { multiLanguageData, loginData } = useSelector((state) => state?.main);
  const [favoriteJobs, setFavoriteJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const LanguageData = multiLanguageData?.JobPostings || {};
  const {
    TEXT_FAVORITE_JOBS = English.JobPostings.TEXT_FAVORITE_JOBS || "Favorite Jobs",
    TEXT_NO_JOB_POSTINGS_FOUND = English.JobPostings.TEXT_NO_JOB_POSTINGS_FOUND
  } = LanguageData || {};

  const fetchFavoriteJobs = async () => {
    if (!loginData?.userInformation?.UserId) return;
    setLoading(true);
    try {
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: "",
        UserId: Number(loginData.userInformation.UserId),
        IncludeRecordNr: true,
        TypeOfObjectReturned: "",
        FetchAllowedRecordsOnly: false,
        SearchList: [
          {
            userId: Number(loginData.userInformation.UserId)
          }
        ],
        SortList: [{}]
      };
      const res = await Postrequestcall(
        FETCH_USER_FAVORITE_JOB_POSTS,
        payload,
        loginData?.accessToken
      );
      const favoriteRecords = res?.data?.data || [];
      const flattenedJobs = favoriteRecords.flatMap((record) =>
        (record.jobList || []).map((job) => ({
          ...job,
          isExternal: job?.isExternal ?? record?.isExternal,
          isFavorite: true,
          favoriteRecordId: record.id,
          favoritesMeta: record
        }))
      );
      setFavoriteJobs(flattenedJobs);
    } catch (error) {
      console.error("fetchFavoriteJobs-> Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFavoriteJobs();
    }
  }, [open, reloadKey]);

  const renderJob = (job) => {
    const jobKey = getJobFavoriteKey({ ...job, isExternal: job?.IsExternal });
    const expiryLabel = getExpiryLabel(job?.jobPostExpiryDate);
    const isFavoriteLoading = favoriteLoadingKeys?.[jobKey];
    const tradeItems = getTradeItems(job)
      .map((trade) => trade?.tradeName || trade?.typeName || trade?.name || "")
      .filter(Boolean);
    const jobType =
      job?.jobPostTypes?.[0]?.typeName ||
      job?.jobEmploymentType ||
      job?.jobEmployementType ||
      "";
    const postedDateLabel = job?.jobPostedDate
      ? moment(job.jobPostedDate).format("MMM DD")
      : job?.dateCreated
        ? moment(job.dateCreated).format("MMM DD")
        : "";
    return (
      <Card
        key={jobKey}
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e0e0e0",
          p: 2,
          backgroundColor: "white"
        }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: "#eaf3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
            <BusinessCenterIcon sx={{ fontSize: 26, color: "#1a73e8" }} />
          </Box>
          <Stack spacing={0.4} flex={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack spacing={0.2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {job?.jobTitle || job?.title || "Untitled"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {job?.companyName || job?.company || ""}
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.25}>
                {jobType !== "" && <Chip
                  label={jobType}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    bgcolor: "#f0f4ff",
                    color: "#1a1a1a",
                    fontWeight: 600,
                    fontSize: "0.7rem"
                  }}
                />}
                <Typography variant="body2" color="textSecondary">
                  {postedDateLabel}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FmdGoodIcon sx={{ fontSize: 16, color: "#666" }} />
              <Typography variant="body2" color="textSecondary">
                {job?.location || job?.locationName || ""}
              </Typography>
            </Stack>
            {tradeItems.length > 0 && (
              <Stack direction="row" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5 }}>
                {tradeItems.slice(0, 3).map((tradeName, index) => (
                  <Chip
                    key={`${tradeName}-${index}`}
                    label={tradeName}
                    size="small"
                    sx={{
                      bgcolor: "#f0f2f5",
                      color: "#333",
                      fontWeight: 500,
                      borderRadius: 1
                    }}
                  />
                ))}
                {tradeItems.length > 3 && (
                  <Chip
                    label={`+${tradeItems.length - 3} more`}
                    size="small"
                    sx={{ bgcolor: "#f0f2f5", color: "#333", borderRadius: 1 }}
                  />
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              {job?.isApplied && (
                <Chip
                  label="Applied"
                  size="small"
                  sx={{
                    bgcolor: "#e7f5ec",
                    color: "#1f7a3d",
                    fontWeight: 600,
                    borderRadius: 1
                  }}
                />
              )}
              {expiryLabel && (
                <Chip
                  label={expiryLabel}
                  size="small"
                  sx={{
                    bgcolor: "#ffe8e8",
                    color: "#c53030",
                    fontWeight: 600,
                    borderRadius: 1
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Box>
            {isFavoriteLoading ? (
              <CircularProgress size={28} />
            ) : (
              <IconButton
                onClick={async (event) => {
                  if (onFavoriteToggle) {
                    await onFavoriteToggle(event, { ...job, isExternal: job?.IsExternal, isFavorite: true });
                    fetchFavoriteJobs();
                  }
                }}
                sx={{ color: "#f5c518" }}>
                <StarIcon />
              </IconButton>
            )}
          </Box>
        </Stack>
      </Card>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="user-modal-responsive" sx={{ maxHeight: "80vh", overflow: "auto" }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{TEXT_FAVORITE_JOBS}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loader />
            </Box>
          ) : favoriteJobs.length > 0 ? (
            <Stack spacing={2}>{favoriteJobs.map(renderJob)}</Stack>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                {TEXT_NO_JOB_POSTINGS_FOUND}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default FavoriteJobsModal;
